import React, { useMemo, useRef } from 'react'
import NextImage from 'next/image'
import { Routes } from '@/assets/configs'
import { TFunction } from 'i18next'

import { EmtFloatingIconButton, EmtModal, useMobile } from 'emt-components-v2'

import LinkButton from '@/components/EMT/LinkButton'

import { StyledSlider } from '@/components/Profile/partials'

import Box from '@mui/material/Box'

import Blur from '@/assets/img/blur.png'

type Props = React.PropsWithChildren<{
  picturesUrls: Array<string | null | undefined>
  initialSlide: number
  open: boolean
  onClose: () => void
  publicProfile?: boolean
  t?: TFunction<'profile', undefined>
}>

export const ImagesSliderModal: React.FC<Props> = ({
  picturesUrls,
  initialSlide,
  open,
  onClose,
  publicProfile = false,
  t,
}) => {
  const safePicturesUrls = picturesUrls.filter((url): url is string => typeof url === 'string' && !!url)
  const [avatarImageUrl, ...profileImagesUrls] = safePicturesUrls
  const [isMobile] = useMobile()
  const sliderRef = useRef<{ slickNext: () => void; slickPrev: () => void }>(null)

  const settings = useMemo(
    () => ({
      arrows: false,
      dots: true,
      infinite: true,
      pagination: false,
      speed: 350,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide,
      adaptiveHeight: true,
      verticalSwiping: false,
      swipeToSlide: true,
    }),
    [initialSlide],
  )

  const visibleProfileImages = useMemo(
    () => (publicProfile ? profileImagesUrls.slice(0, 3) : profileImagesUrls),
    [profileImagesUrls, publicProfile],
  )

  return (
    <EmtModal
      open={open}
      variant="carousel"
      onClose={onClose}
      sx={{ zIndex: 9999, '& .emt-x': { top: '50px !important', zIndex: 9999, right: '30px !important' } }}
    >
      <Box
        sx={{
          alignItems: 'center',
          gap: isMobile ? '15px' : '10px',
          height: isMobile ? '100%' : 'auto',
        }}
      >
        <StyledSlider
          ref={sliderRef}
          {...settings}
          sx={{
            top: isMobile && '50%',
            transform: isMobile && 'translateY(-50%) !important',
          }}
        >
          {avatarImageUrl ? (
            <Box
              sx={{
                position: 'relative',
                height: '80vh',
                width: '100% !important',
                touchAction: 'pan-x',
                overscrollBehaviorY: 'none',
                '& img': {
                  position: 'relative',
                  alignSelf: 'center',
                  objectFit: 'contain',
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                  touchAction: 'pan-x',
                },
              }}
            >
              <NextImage
                placeholder="blur"
                blurDataURL={Blur.blurDataURL}
                src={avatarImageUrl}
                quality={50}
                layout="fill"
                sizes="100vw"
                alt={''}
              />
            </Box>
          ) : null}

          {visibleProfileImages.length
            ? visibleProfileImages.map((image, key) => (
                <Box
                  key={key}
                  sx={{
                    position: 'relative',
                    height: '80vh',
                    width: '100% !important',
                    '& img': { position: 'relative', alignSelf: 'center', objectFit: 'contain' },
                  }}
                >
                  <NextImage
                    placeholder="blur"
                    blurDataURL={Blur.blurDataURL}
                    src={image}
                    quality={50}
                    layout="fill"
                    sizes="100vw"
                    alt={''}
                    className={publicProfile && visibleProfileImages.length > 2 && key === 2 ? 'blur' : ''}
                  />
                  {publicProfile && visibleProfileImages.length > 2 && key === 2 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-[20px]">
                      <p className="text-white text-[48px] font-bold ">+{visibleProfileImages.length - 2}</p>
                      {t && (
                        <div className="flex flex-col gap-[15px]">
                          <LinkButton
                            href={Routes.Connection.SignIn}
                            variant="whiteOutlined"
                            size={'medium'}
                            sx={{ width: '260px', maxWidth: '55vw' }}
                          >
                            {t('sign-in.title')}
                          </LinkButton>

                          <LinkButton
                            href={Routes.Connection.SignUp.Type}
                            size={'medium'}
                            sx={{ width: '260px', maxWidth: '55vw' }}
                          >
                            {t('sign-up.title')}
                          </LinkButton>
                        </div>
                      )}
                    </div>
                  )}
                </Box>
              ))
            : null}
        </StyledSlider>
        {visibleProfileImages.length ? (
          <>
            <EmtFloatingIconButton
              boxShadow
              iconClass="emt-arrow-left"
              sx={{
                position: 'absolute',
                top: '50%',
                left: 35,
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                '& .emt-icons': { fontSize: '18px' },
              }}
              onClick={() => sliderRef.current?.slickPrev()}
            />
            <EmtFloatingIconButton
              boxShadow
              iconClass="emt-arrow-right"
              sx={{
                position: 'absolute',
                top: '50%',
                left: 'inherit',
                right: 35,
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                '& .emt-icons': { fontSize: '18px' },
              }}
              onClick={() => sliderRef.current?.slickNext()}
            />
          </>
        ) : null}
      </Box>
    </EmtModal>
  )
}
